import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Empty,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import {
  ApiOutlined,
  BankOutlined,
  BellOutlined,
  ClearOutlined,
  PlusOutlined,
  ReloadOutlined,
  RightOutlined,
  ToolOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { CardHeader, PageHeader } from 'components/headings';
import CertificacionBadge from 'components/admin/compliance/CertificacionBadge';
import EquipoCreateDrawer from 'components/admin/Equipos/EquipoCreateDrawer';
import EquipoDetailDrawer from 'components/admin/Equipos/EquipoDetailDrawer';
import {
  TIPO_EQUIPO_COLORS,
  TIPO_EQUIPO_LABELS,
} from 'components/admin/Equipos/tipoEquipoCategoria';
import { useCliente } from 'providers/Clientes/ClienteProvider';
import { useColeccion } from 'providers/ColeccionProvider';
import { useEdificios } from 'providers/Edificios';
import { useRegulatoryAlerts } from 'providers/RegulatoryAlerts';
import useTenantFlags from 'hooks/useTenantFlags';
import useAuth from 'hooks/useAuth';
import { buildClienteTabPath } from 'utils/clienteNav';

// Severidad jerárquica para agregar alerts por equipo: la severidad máxima
// en un conjunto manda el color del badge.
const SEVERITY_RANK = { critical: 3, warning: 2, info: 1 };
const SEVERITY_COLORS = {
  critical: 'red',
  warning: 'orange',
  info: 'blue',
};
const getTopSeverity = (alerts) => {
  let top = null;
  let topRank = 0;
  for (const a of alerts) {
    const rank = SEVERITY_RANK[a.severidad] ?? 0;
    if (rank > topRank) {
      topRank = rank;
      top = a.severidad;
    }
  }
  return top;
};

const { Text } = Typography;
const { useToken } = theme;

// Tab "Equipos" — refactor del form original a una tabla agrupada por
// edificio. Click en una fila abre el EquipoDetailDrawer con tabs internos.
// Soporta filtro por edificio via ?edificio=X (set desde EdificiosPage al
// hacer click en "Ver equipos del edificio").
const AscensoresPage = ({ id }) => {
  const { token } = useToken();
  const { readOnly } = useTenantFlags();
  const { user } = useAuth();
  const isAdmin = user?.is_administrador;
  const location = useLocation();
  const navigate = useNavigate();
  const { ascensores, loadingAscensores, callAscensores } = useCliente();
  const { edificios, loading: loadingEdificios, refresh: refreshEdificios } = useEdificios(id);
  const { alerts: clientAlerts, refresh: refreshAlerts } = useRegulatoryAlerts(id);
  const { findItemsByType } = useColeccion();
  const [plantillas, setPlantillas] = useState([]);
  const [selectedEquipo, setSelectedEquipo] = useState(null);
  const [drawerDefaultTab, setDrawerDefaultTab] = useState(undefined);
  // Estado del drawer de creación. `edificioPreseleccionado` se setea desde
  // el botón contextual por edificio; el botón global lo deja null (el form
  // pedirá elegir edificio via Select obligatorio).
  const [createOpen, setCreateOpen] = useState(false);
  const [createEdificioId, setCreateEdificioId] = useState(null);

  const openCreate = (edificioId) => {
    setCreateEdificioId(edificioId ?? null);
    setCreateOpen(true);
  };
  const closeCreate = () => {
    setCreateOpen(false);
    setCreateEdificioId(null);
  };
  const onEquipoCreated = () => {
    closeCreate();
    // callAscensores ya corre desde EquipoDatosForm; refrescamos también
    // edificios para que el conteo "X equipos" se actualice inmediatamente.
    refreshEdificios();
  };

  const tieneEdificios = (edificios?.length ?? 0) > 0;

  const edificioFilter = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('edificio');
    const parsed = raw ? Number(raw) : null;
    return Number.isFinite(parsed) ? parsed : null;
  }, [location.search]);

  useEffect(() => {
    callAscensores?.(id);
  }, [callAscensores, id]);

  // Carga única de plantillas de plan para mapear id_tipo_ascensor → nombre.
  useEffect(() => {
    let cancel = false;
    findItemsByType('ACTIVIDAD').then((data) => {
      if (!cancel) setPlantillas(Array.isArray(data) ? data : []);
    });
    return () => {
      cancel = true;
    };
  }, [findItemsByType]);

  const plantillasById = useMemo(() => {
    const m = new Map();
    for (const p of plantillas) m.set(p.id, p);
    return m;
  }, [plantillas]);

  // Mapa id_edificio → lista de equipos (incluyendo null para sueltos).
  const groupedByEdificio = useMemo(() => {
    const map = new Map();
    (ascensores ?? []).forEach((a) => {
      const key = a?.id_edificio ?? null;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(a);
    });
    return map;
  }, [ascensores]);

  // Edificios a renderizar: todos los del cliente + un grupo "sin asignar" si
  // hay equipos huérfanos. Si viene filtro por URL, sólo se muestra ese.
  const edificiosToRender = useMemo(() => {
    const base = edificios ?? [];
    const orphans = groupedByEdificio.get(null) ?? [];
    const list = [...base];
    if (orphans.length > 0) {
      list.push({ id: null, nombre: 'Sin edificio asignado', direccion: '', _orphan: true });
    }
    if (edificioFilter) {
      return list.filter((e) => e.id === edificioFilter);
    }
    return list;
  }, [edificios, edificioFilter, groupedByEdificio]);

  const clearFilter = () => {
    navigate(buildClienteTabPath(isAdmin, id, 'ascensores'));
  };
  const refresh = async () => {
    await Promise.all([callAscensores?.(id), refreshEdificios(), refreshAlerts()]);
  };

  // Index alerts por id_equipo para render O(1) en la columna de la tabla.
  // Una sola llamada GET /clientes/:id/alerts alimenta a todos los badges —
  // evita N+1 por equipo.
  const alertsByEquipo = useMemo(() => {
    const map = new Map();
    for (const a of clientAlerts ?? []) {
      if (!a.id_equipo) continue;
      if (!map.has(a.id_equipo)) map.set(a.id_equipo, []);
      map.get(a.id_equipo).push(a);
    }
    return map;
  }, [clientAlerts]);

  const openEquipoDrawer = (row, tab) => {
    setDrawerDefaultTab(tab);
    setSelectedEquipo(row);
  };

  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      ellipsis: true,
      width: 220,
      render: (nombre) => <Text strong>{nombre}</Text>,
    },
    {
      title: 'Plan',
      dataIndex: 'id_tipo_ascensor',
      width: 200,
      render: (idPlan, row) => {
        // Zonas/ubicaciones (requiere_plan=false) son creadas a propósito
        // sin plan desde el editor de ODT — no son equipos reales. No deben
        // figurar como "sin plan problemático".
        if (row.requiere_plan === false || row.requiere_plan === 0) {
          return (
            <Tooltip title="Zona o ubicación — no requiere plan de mantención. Si es un equipo real, activá 'Requiere plan' y asignale una plantilla.">
              <Tag color="default">Zona / ubicación</Tag>
            </Tooltip>
          );
        }
        if (!idPlan) {
          return (
            <Tooltip title="Equipo real sin plantilla asignada. No participará en mantenciones automáticas hasta que se asigne un plan.">
              <Tag color="red" icon={<WarningOutlined />} style={{ fontWeight: 600 }}>
                Sin plan
              </Tag>
            </Tooltip>
          );
        }
        const plantilla = plantillasById.get(idPlan);
        return (
          <Tag color="blue" icon={<ToolOutlined />} style={{ fontWeight: 600, maxWidth: '100%' }}>
            {plantilla?.nombre ?? `Plantilla #${idPlan}`}
          </Tag>
        );
      },
    },
    {
      title: 'Categoría',
      dataIndex: 'tipo_equipo_categoria',
      width: 160,
      render: (t) => (
        <Tag color={TIPO_EQUIPO_COLORS[t] ?? 'default'}>
          {TIPO_EQUIPO_LABELS[t] ?? t ?? 'Sin categoría'}
        </Tag>
      ),
    },
    {
      title: 'Marca / Modelo',
      key: 'marca_modelo',
      ellipsis: true,
      render: (_, r) =>
        r.marca || r.modelo ? (
          `${r.marca ?? '—'} / ${r.modelo ?? '—'}`
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Registro MINVU',
      dataIndex: 'num_registro_minvu',
      width: 150,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Próxima cert.',
      dataIndex: 'prox_cert',
      width: 170,
      render: (fecha) =>
        fecha ? <CertificacionBadge fecha={fecha} size="sm" /> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      width: 130,
      render: (s) => (s ? <Tag>{s}</Tag> : <Text type="secondary">—</Text>),
    },
    {
      title: 'Alertas',
      key: 'alerts',
      width: 110,
      align: 'center',
      render: (_, row) => {
        const equipoAlerts = alertsByEquipo.get(row.id) ?? [];
        if (equipoAlerts.length === 0) {
          return <Text type="secondary">—</Text>;
        }
        const top = getTopSeverity(equipoAlerts) ?? 'info';
        const tooltip = equipoAlerts
          .slice(0, 3)
          .map((a) => `• ${a.mensaje}`)
          .join('\n');
        return (
          <Tooltip
            title={
              <div style={{ whiteSpace: 'pre-line' }}>
                {tooltip}
                {equipoAlerts.length > 3 ? `\n...y ${equipoAlerts.length - 3} más` : ''}
              </div>
            }
          >
            <Button
              type="text"
              size="small"
              icon={<BellOutlined style={{ color: SEVERITY_COLORS[top] }} />}
              onClick={(e) => {
                e.stopPropagation();
                openEquipoDrawer(row, 'alertas');
              }}
            >
              <Badge
                count={equipoAlerts.length}
                color={SEVERITY_COLORS[top]}
                overflowCount={9}
                style={{ boxShadow: 'none' }}
              />
            </Button>
          </Tooltip>
        );
      },
    },
    {
      title: '',
      key: 'open',
      width: 40,
      render: () => <RightOutlined style={{ color: token.colorTextTertiary }} />,
    },
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: token.marginLG,
        paddingTop: token.paddingMD,
        paddingBottom: token.paddingXL,
      }}
    >
      <PageHeader
        icon={ApiOutlined}
        title="Equipos"
        subtitle="Ascensores, escaleras mecánicas, funiculares, rampas y montacargas"
        actions={
          <Space wrap>
            {edificioFilter ? (
              <Button icon={<ClearOutlined />} onClick={clearFilter}>
                Quitar filtro de edificio
              </Button>
            ) : null}
            <Button
              icon={<ReloadOutlined />}
              onClick={refresh}
              loading={loadingAscensores || loadingEdificios}
            >
              Actualizar
            </Button>
            {!readOnly && (
              <Tooltip
                title={
                  tieneEdificios ? undefined : 'Primero crea un edificio en el tab "Edificios"'
                }
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => openCreate(edificioFilter ?? null)}
                  disabled={!tieneEdificios}
                >
                  Nuevo equipo
                </Button>
              </Tooltip>
            )}
          </Space>
        }
      />

      {edificioFilter && edificiosToRender.length === 0 ? (
        <Alert
          type="warning"
          showIcon
          message="Edificio no encontrado"
          description="El edificio solicitado no existe para este cliente."
          action={
            <Button size="small" onClick={clearFilter}>
              Ver todos
            </Button>
          }
        />
      ) : null}

      {edificiosToRender.length === 0 && !edificioFilter ? (
        <Empty description="Este cliente todavía no tiene edificios ni equipos registrados." />
      ) : null}

      {edificiosToRender.map((ed) => {
        const equipos = groupedByEdificio.get(ed.id) ?? [];
        return (
          <div
            key={ed.id ?? 'sin-asignar'}
            style={{
              border: `1px solid ${token.colorBorder}`,
              borderRadius: token.borderRadiusLG,
              overflow: 'hidden',
              background: token.colorBgContainer,
              boxShadow: token.boxShadowSecondary,
            }}
          >
            <CardHeader
              icon={BankOutlined}
              tint={ed._orphan ? token.colorWarning : token.colorPrimary}
              title={ed.nombre}
              subtitle={ed.direccion || 'Sin dirección registrada'}
              action={
                <Space size="small">
                  <Tag style={{ margin: 0 }}>
                    {equipos.length} {equipos.length === 1 ? 'equipo' : 'equipos'}
                  </Tag>
                  {!ed._orphan && !readOnly ? (
                    <Button size="small" icon={<PlusOutlined />} onClick={() => openCreate(ed.id)}>
                      Añadir equipo
                    </Button>
                  ) : null}
                </Space>
              }
            />
            <Table
              rowKey="id"
              size="small"
              columns={columns}
              dataSource={equipos}
              pagination={false}
              loading={loadingAscensores}
              onRow={(row) => ({
                onClick: () => openEquipoDrawer(row, undefined),
                style: { cursor: 'pointer' },
              })}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Sin equipos en este edificio."
                  />
                ),
              }}
            />
          </div>
        );
      })}

      <EquipoDetailDrawer
        open={!!selectedEquipo}
        equipo={selectedEquipo}
        idCliente={id}
        defaultTab={drawerDefaultTab}
        onClose={() => {
          setSelectedEquipo(null);
          setDrawerDefaultTab(undefined);
          refreshAlerts();
        }}
        onSaved={() => {
          callAscensores?.(id);
          refreshAlerts();
          setSelectedEquipo(null);
          setDrawerDefaultTab(undefined);
        }}
      />

      <EquipoCreateDrawer
        open={createOpen}
        idCliente={id}
        preselectedEdificioId={createEdificioId}
        onClose={closeCreate}
        onCreated={onEquipoCreated}
      />
    </div>
  );
};

export default AscensoresPage;
